import AddIcon from '@mui/icons-material/Add';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdsComponent from "../../components/AdComponent";
import CustomButton from '../../components/CustomButton';
import CustomCard from '../../components/CustomCard';
import ResponsiveTable from '../../components/CustomResponsiveTable';
import CustomSelect from '../../components/CustomSelect';
import CustomSwitch from '../../components/CustomSwitch';
import CustomTextField from '../../components/CustomTextField';
import CustomTypography from '../../components/CustomTypography';
import { triggerGTMEvent } from "../../components/GTMEvent";
import { CurrencyFormatterUtils } from '../currency-formatter/currency-formatter-utils';
import * as Data from './prepayment-calculator-data';
import { setBonusAmount, setLoans, setSelectedStrategy } from './prepayment-calculator-reducer'; // Path to your reducer
import * as Utils from './prepayment-calculator-utils';


const Example = ({ router }) => {
    const dispatch = useDispatch();
    const loans = useSelector((state) => state.prepaymentCalculator.loans || []);
    const selectedStrategy = useSelector((state) => state.prepaymentCalculator.selectedStrategy);
    const bonusAmount = useSelector((state) => state.prepaymentCalculator.bonusAmount);
    const currencyFormatter = useSelector((state) => state.currencyDataFormatter.formatter || {});
    const [report, setReport] = useState(Utils.generateReport(bonusAmount, loans, selectedStrategy));
    const [selectedLoan, setSelectedLoan] = useState(report && report.reportLoans[0] ? report.reportLoans[0] : null);
    const sectionRef = useRef(null);
    const [openDialogForLoanUpdate, setOpenDialogForLoanUpdate] = useState(false);
    const [selectedRowForUpdate, setSelectedRowForUpdate] = useState(null);
    const [openDialogForLoanDelete, setOpenDialogForLoanDelete] = useState(false);
    const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);

    const scrollToTopRef = useRef(null);
    useEffect(() => {
        scrollToTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);


    const navigateTo = (path) => {
        router.navigate(path); // This navigates to the specific page
    };

    const handleAddNewLoan = (newLoan) => {
        triggerGTMEvent("Input Data", "PREPAYMENT > Add New Loan");
        newLoan.id = Math.floor(Math.random() * (100001));
        newLoan.isInterestedInLoanPayment = true;
        dispatch(setLoans([...loans, newLoan]));
        setReport(null);
    };

    const handleDeleteLoan = (id) => {
        triggerGTMEvent("Input Data", "PREPAYMENT > Delete Existing Loan");
        const updatedLoans = loans.filter((row) => row.id !== id);
        dispatch(setLoans(updatedLoans));
        setReport(null);
    };

    const handleUpdateLoan = (updatedLoan) => {
        triggerGTMEvent("Input Data", "PREPAYMENT > Update Existing Loan");
        updatedLoan.emi = Utils.calculateEMI(updatedLoan.outstandingAmount, updatedLoan.interestRate, updatedLoan.remainingTerm);
        const updatedLoans = loans.map(loan => loan.id === updatedLoan.id ? { ...loan, ...updatedLoan } : loan)
        dispatch(setLoans(updatedLoans));
        setReport(null);
        return updatedLoan;
    };

    const handleBonusAmount = (event) => {
        setReport(null);
        dispatch(setBonusAmount(event.target.value));
    };

    const handleSelectedStrategy = (event) => {
        setReport(null);
        dispatch(setSelectedStrategy(event.target.value));
    };

    const handleOpenDialogForLoanUpdate = (row) => {
        setSelectedRowForUpdate(row);
        setOpenDialogForLoanUpdate(true);
    };
    const handleCloseDialogForLoanUpdate = () => {
        setOpenDialogForLoanUpdate(false);
        setSelectedRowForUpdate(null);
    };
    const handleSaveChanges = () => {
        if (selectedRowForUpdate.id) {
            handleUpdateLoan(selectedRowForUpdate);
        } else {
            handleAddNewLoan(selectedRowForUpdate);
        }
        handleCloseDialogForLoanUpdate();
    };
    const handleFieldChange = (e) => {
        let updatedRow = {
            ...selectedRowForUpdate,
            [e.target.name]: e.target.value,
        }
        if (updatedRow.outstandingAmount && updatedRow.interestRate && updatedRow.remainingTerm &&
            updatedRow.outstandingAmount > 0 && updatedRow.interestRate > 0 && updatedRow.remainingTerm > 0
        ) {
            updatedRow.emi = Utils.calculateEMI(updatedRow.outstandingAmount, updatedRow.interestRate, updatedRow.remainingTerm);
        }
        setSelectedRowForUpdate(updatedRow);
    };
    const handleIsInterestedInLoanPayment = (id) => {
        setSelectedRowForUpdate({
            ...selectedRowForUpdate,
            isInterestedInLoanPayment: !selectedRowForUpdate.isInterestedInLoanPayment
        })
    };

    const handleOpenDialogForLoanDelete = (id) => {
        setSelectedRowForDelete(id);
        setOpenDialogForLoanDelete(true);
    };
    const handleCloseDialogForLoanDelete = () => {
        setOpenDialogForLoanDelete(false);
        setSelectedRowForDelete(null);
    };
    const handleConfirmDelete = () => {
        handleDeleteLoan(selectedRowForDelete);
        handleCloseDialogForLoanDelete();
    };

    const generateReport = () => {
        triggerGTMEvent("Generate Report", "PREPAYMENT > Generate Report");
        const report = Utils.generateReport(bonusAmount, loans, selectedStrategy);
        setSelectedLoan(report.reportLoans[0]);
        setReport(report);
        setTimeout(() => {
            scrollToSection();
        }, 1000);
    };

    const scrollToSection = () => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div ref={scrollToTopRef}>
            {/* BREADCRUMPS WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }} sx={{ margin: '10px' }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" onClick={() => navigateTo('/')}>
                            Home
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Prepayment Calculator</Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
            {/* BREADCRUMPS WIDGET ENDS */}

            {/* INPUT WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomCard
                        title="Loan Prepayment Calculator"
                        description="Optimize your loan repayments and save on interest with smart strategies."
                    >
                        <Grid container spacing={1} >
                            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                                <CustomTextField
                                    label="Enter savings / bonus amount you have"
                                    value={bonusAmount}
                                    type="number"
                                    onChange={handleBonusAmount}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                                <CustomSelect
                                    label="Choose a strategy"
                                    value={selectedStrategy}
                                    onChange={handleSelectedStrategy}
                                    options={Data.strategyOptions}
                                />
                            </Grid>
                        </Grid>

                        <CustomTypography variant="h6" >Add or update your loans here!</CustomTypography>
                        <Grid container spacing={1}>
                            {loans && loans.map((loanDetails, index) =>
                                <Grid item size={{ xs: 12, sm: 6, md: 6 }} key={index}>
                                    <ResponsiveTable data={Data.yourLoansTableData(loanDetails, currencyFormatter, handleOpenDialogForLoanUpdate, handleOpenDialogForLoanDelete)} />
                                </Grid>
                            )}
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item size={{ xs: 12, sm: 12, md: 8 }}></Grid>
                            <Grid item size={{ xs: 12, sm: 12, md: 4 }} sx={{ marginTop: '10px' }}>
                                <CustomButton
                                    variant="contained"
                                    color="primary"
                                    onClick={() => { setOpenDialogForLoanUpdate(true); setSelectedRowForUpdate({ isInterestedInLoanPayment: true }) }}
                                    startIcon={<AddIcon />}>
                                    Add New Loan
                                </CustomButton>
                            </Grid>
                        </Grid>
                    </CustomCard>
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomCard>
                        <CustomButton
                            variant="contained"
                            color="success"
                            onClick={generateReport}
                            fullWidth={true}
                        >
                            GENERATE REPORT
                        </CustomButton>
                    </CustomCard>
                </Grid>
            </Grid>
            {/* INPUT WIDGET END */}

            {/* REPORT WIDGET START */}
            <div ref={sectionRef}>
                {report && report.reportLoans &&
                    <Grid container spacing={1}>
                        <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                            <CustomCard
                                title="Report Summary"
                                description="You need to split the bonus and prepay the loans as outlined below."
                            >
                                <ResponsiveTable
                                    data={Data.actionsTableData(report, currencyFormatter)}
                                />
                            </CustomCard>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                            <CustomCard
                                title="Percentage Savings"
                                description="By prepaying your loans, you will save a total interest of: "

                            >
                                <Typography variant="h4" color="success" fontWeight="bold">
                                    {Data.getPercentageOfSavingsOnInterest(report)}
                                </Typography>
                            </CustomCard>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                            <CustomCard
                                title="Monetary Savings"
                                description="By prepaying your loans, you will save a total interest of: "

                            >
                                <Typography variant="h4" color="success" fontWeight="bold">
                                    {CurrencyFormatterUtils(Data.getAmountOfSavingsOnInterest(report), currencyFormatter)}
                                </Typography>
                            </CustomCard>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                            <CustomCard title="Detailed Report">
                                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                                    <Grid container spacing={1}>
                                        <Grid item size={{ xs: 12, sm: 12, md: 4 }}>
                                            <Stack spacing={1}> {/* Stack ensures vertical alignment */}
                                                {report.reportLoans.map((loan) => (
                                                    <Chip
                                                        key={loan.loanType}
                                                        label={loan.loanType}
                                                        onClick={() => setSelectedLoan(loan)}
                                                        color={selectedLoan.loanType === loan.loanType ? 'primary' : 'default'}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Grid>
                                        <Grid item size={{ xs: 12, sm: 12, md: 8 }}>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                1. Loan Tenure Reduction: <strong>{Utils.formatYearsMonths(selectedLoan.originalTenure)} </strong>
                                                → <strong> {Utils.formatYearsMonths(selectedLoan.adjustedTenure)}.</strong>
                                                <br />
                                                Difference:  <strong style={{ color: 'green' }}> {Utils.formatYearsMonths(selectedLoan.varianceTenure)}.</strong>
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                2. Interest Payable Reduction: <strong>{CurrencyFormatterUtils(selectedLoan.originalInterestPayable, currencyFormatter)} </strong> →
                                                <strong> {CurrencyFormatterUtils(selectedLoan.adjustedInterestPayable, currencyFormatter)} </strong>
                                                <br />
                                                Saved: <strong style={{ color: 'green' }}>{CurrencyFormatterUtils(selectedLoan.varianceInterestPayable, currencyFormatter)} </strong>
                                            </Typography>
                                            <Typography variant="body1">
                                                3. Outstanding Loan Amount Reduction: <strong> {CurrencyFormatterUtils(selectedLoan.originalOutstandingAmount, currencyFormatter)} </strong> →
                                                <strong> {CurrencyFormatterUtils(selectedLoan.adjustedOutstandingAmount, currencyFormatter)} </strong>
                                                <br />
                                                Reduced by: <strong style={{ color: 'green' }}>{CurrencyFormatterUtils(selectedLoan.varianceOutstandingAmount, currencyFormatter)} </strong>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CustomCard>
                        </Grid>
                    </Grid>
                }
            </div>
            {/* REPORT WIDGET END */}

            {/* AD WIDGET START */}
            {/* <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <AdsComponent dataAdSlot='5249192362' />
                </Grid>
            </Grid> */}
            {/* AD WIDGET END */}

            {/* BLOG WIDGET START */}
            <CustomCard>
                <Typography variant="h6" gutterBottom>
                    How Loan Prepayment Can Save You Huge Money in Interest
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                    Loan prepayment is a powerful strategy to reduce your overall interest burden and close your loans faster.
                    However, to maximize savings, it’s important to use the **Annual Interest Percentage Reduction (AIPR)** approach.
                    This ensures that your extra payments are directed toward loans with the highest interest savings potential.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    What is AIPR?
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                    AIPR helps identify which loan should be prepaid first to save the most interest. Instead of simply paying off loans with the highest
                    outstanding balance or maximum interest paid, AIPR focuses on the actual reduction in future interest payments.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    Use Cases
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                    Here’s a comparison of three different loan prepayment strategies:
                </Typography>

                <Typography variant="body1">Case 1: Prepaying the Loan with the Highest AIPR</Typography>
                <Typography variant="body1" paragraph>
                    This approach ensures that every extra rupee paid reduces the maximum amount of interest. As a result, you will save the most in the long run.
                </Typography>

                <Typography variant="body1">Case 2: Prepaying the Loan with Maximum Interest</Typography>
                <Typography variant="body1" paragraph>
                    While it may seem logical to prepay the loan with the highest total interest paid, it might not always be the best option if the AIPR is lower.
                </Typography>

                <Typography variant="body1">Case 3: Prepaying the Loan with the Lowest Outstanding Amount</Typography>
                <Typography variant="body1" paragraph>
                    Many borrowers prefer closing smaller loans first for psychological relief. However, this method often results in minimal interest savings.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    Conclusion
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                    The best way to optimize your prepayments is by focusing on the loan with the highest AIPR. This ensures maximum savings and a faster loan closure.
                </Typography>


                <Typography variant="body1" color="textSecondary" display="block" sx={{ mt: 3 }}>
                    *Disclaimer: These calculations do not account for prepayment penalties, processing fees, or tax benefits
                    associated with home loan interest or principal repayments. Please consult with your lender for exact figures.
                </Typography>
            </CustomCard>
            {/* BLOG WIDGET END */}

            {/* MODEL WIDGET START */}
            <Dialog open={openDialogForLoanUpdate} onClose={handleCloseDialogForLoanUpdate} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Loan</DialogTitle>
                <DialogContent dividers>
                    {selectedRowForUpdate && (
                        <>
                            <CustomTextField
                                name="loanType"
                                label="Loan Type"
                                value={selectedRowForUpdate.loanType || ''}
                                onChange={handleFieldChange}
                            />
                            <CustomTextField
                                name="outstandingAmount"
                                label="Outstanding Amount"
                                type="number"
                                value={selectedRowForUpdate.outstandingAmount || ''}
                                onChange={handleFieldChange}
                            />
                            <CustomTextField
                                name="remainingTerm"
                                label="Remaining Term (in months)"
                                type="number"
                                value={selectedRowForUpdate.remainingTerm || ''}
                                onChange={handleFieldChange}
                            />
                            <CustomTextField
                                name="interestRate"
                                label="Interest Rate"
                                type="number"
                                value={selectedRowForUpdate.interestRate || ''}
                                onChange={handleFieldChange}
                            />
                            <CustomTextField
                                name="emi"
                                label="EMI"
                                type="number"
                                disabled={true}
                                value={selectedRowForUpdate.emi || ''}
                                onChange={handleFieldChange}
                            />
                            <CustomSwitch
                                label="Interested to prepay the loan?"
                                value={selectedRowForUpdate.isInterestedInLoanPayment}
                                handleSwitchChange={() => handleIsInterestedInLoanPayment(selectedRowForUpdate.id)}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogForLoanUpdate} color="secondary">Cancel</Button>
                    <Button onClick={handleSaveChanges} color="primary" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDialogForLoanDelete}
                onClose={handleCloseDialogForLoanDelete}
                aria-labelledby="confirm-delete-title"
                aria-describedby="confirm-delete-description"
            >
                <DialogTitle id="confirm-delete-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-delete-description">
                        Are you sure you want to delete this loan? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogForLoanDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            {/* MODEL WIDGET END */}
        </div>
    );
};

export default Example;
